import React, { useState, useEffect, useMemo } from "react";
import { GetPriceList } from "../../../server/PriceList";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

const PricesCompareTable = ({ selectedProd }) => {
  const [company, setCompany] = useState([]);
  const { pathname } = useLocation();

  const { role } = useSelector((state) => ({
    role: state.user.role,
  }));

  useEffect(() => {
    let cancelled = false;

    const parseRows = (data) => {
      return Object.entries(data).flatMap(([retailer, retailerData]) => {
        const branches = retailerData?.branches;
        if (!branches || typeof branches !== "object") return [];

        return Object.entries(branches).flatMap(([branch, branchData]) =>
          (branchData?.prices || []).map((price) => ({
            retailer,
            branch,
            retailerName: branchData?.retailerName || retailer,
            branchName: branchData?.branchName,
            price: price.price,
            date: price.date,
            promotionDescription: price.promotionDescription,
          })),
        );
      });
    };

    (async () => {
      try {
        const gtin = selectedProd?.gtin;
        if (gtin == null || String(gtin).trim() === "") {
          if (!cancelled) setCompany([]);
          return;
        }

        const data = await GetPriceList(gtin, role);
        if (cancelled) return;
        if (!data || typeof data !== "object") {
          setCompany([]);
          return;
        }

        let formattedData = parseRows(data);

        const shaareiRevachaEntries = formattedData.filter((entry) =>
          entry.retailerName?.includes("שערי רווחה"),
        );

        if (
          shaareiRevachaEntries.length === 2 &&
          shaareiRevachaEntries[0].price === shaareiRevachaEntries[1].price
        ) {
          shaareiRevachaEntries[0].branchName = null;
          formattedData = formattedData.filter(
            (entry) => entry !== shaareiRevachaEntries[1],
          );
        }

        if (cancelled) return;
        setCompany(
          formattedData.sort((a, b) => {
            const pa = parseFloat(a.price);
            const pb = parseFloat(b.price);
            return (
              (Number.isFinite(pa) ? pa : 0) - (Number.isFinite(pb) ? pb : 0)
            );
          }),
        );
      } catch {
        if (!cancelled) setCompany([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedProd, role]);

  const hasPromotion = selectedProd.pricing?.promotions?.length > 0;
  const promo = hasPromotion ? selectedProd.pricing.promotions[0] : null;
  const originalPrice = selectedProd.pricing?.price
    ? parseFloat(selectedProd.pricing.price)
    : null;

  const getSalePrice = () => {
    if (!promo || originalPrice == null) return null;
    switch (promo.promoType) {
      case "FLAT":
        return parseFloat(promo.discountValue);
      case "PERCENT":
        return originalPrice * (1 - promo.discountValue / 100);
      default:
        return null;
    }
  };

  const getPromoLabel = () => {
    if (!promo) return null;
    switch (promo.promoType) {
      case "FLAT":
        return `מחיר מבצע: ${parseFloat(promo.discountValue).toFixed(2)} ₪`;
      case "PERCENT":
        return `${promo.discountValue}% הנחה`;
      case "QUANTITY_PRICE":
        return `${promo.minQuantity} ב־${promo.discountValue} ₪`;
      default:
        return null;
    }
  };

  const salePrice = getSalePrice();
  const promoLabel = getPromoLabel();
  const hamazonPrice =
    salePrice != null
      ? salePrice.toFixed(2)
      : originalPrice != null
        ? originalPrice.toFixed(2)
        : null;
  const showHamazonStrike =
    hasPromotion &&
    promo.promoType !== "QUANTITY_PRICE" &&
    originalPrice != null;

  const { onlineStores, physicalStores } = useMemo(() => {
    const online = [];
    const physical = [];

    company.forEach((store) => {
      if (store.retailerName?.includes("המזון")) return;

      const name = store.branchName
        ? `${store.retailerName} - ${store.branchName}`
        : store.retailerName;

      const hasStorePromo =
        store.promotionDescription &&
        /^\d+(\.\d{1,2})? ₪$/.test(store.promotionDescription);

      const entry = {
        name,
        price: parseFloat(store.price).toFixed(2),
        hasStorePromo,
        promotionDescription: store.promotionDescription || null,
      };

      if (name.includes("אונליין")) {
        online.push(entry);
      } else {
        physical.push(entry);
      }
    });

    return { onlineStores: online, physicalStores: physical };
  }, [company]);

  const renderStorePrice = (store) => (
    <td className="price-table__price">
      {(store.hasStorePromo || !store.promotionDescription) && (
        <span
          className={store.hasStorePromo ? "price-table__original" : undefined}
        >
          {store.price} ₪
        </span>
      )}
      {store.promotionDescription && (
        <span className="store-promo-desc">{store.promotionDescription}</span>
      )}
    </td>
  );

  if (pathname.startsWith("/edit-product") && company.length === 0) {
    return null;
  }

  if (hamazonPrice == null && company.length === 0) {
    return null;
  }

  const hasCompetitorPrices =
    onlineStores.length > 0 || physicalStores.length > 0;

  return (
    <div className="price-comparison">
      {hamazonPrice != null && (
        <div className="price-hero">
          <span className="price-hero__label">המזון — המחיר שלנו</span>
          <div className="price-hero__row">
            {showHamazonStrike && (
              <span className="price-hero__original">
                {originalPrice.toFixed(2)} ₪
              </span>
            )}
            <div
              className={`price-hero__price${showHamazonStrike && salePrice != null ? " price-hero__price--sale" : ""}`}
            >
              {hamazonPrice} <span>₪</span>
            </div>
            {promoLabel && <span className="promo-badge">{promoLabel}</span>}
          </div>
        </div>
      )}

      {hasCompetitorPrices && (
        <div className="price-cols">
          <div>
            <p className="price-col-label">רשתות אונליין</p>
            <div className="price-card price-card--online">
              <table className="price-table">
                <tbody>
                  {onlineStores.map((s) => (
                    <tr key={s.name}>
                      <td>{s.name}</td>
                      {renderStorePrice(s)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <p className="price-col-label">סניפים פיזיים</p>
            <div className="price-card price-card--physical">
              <table className="price-table">
                <tbody>
                  {physicalStores.map((s) => (
                    <tr key={s.name}>
                      <td>{s.name}</td>
                      {renderStorePrice(s)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricesCompareTable;
